import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextInput, Toggle, Select, Box, Flex } from "orcs-design-system";

const generateId = () =>
  `key-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeGet("space.r")};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${themeGet("colors.greyDark")};
`;

const RequiredBadge = styled.span`
  font-size: 10px;
  color: ${themeGet("colors.danger")};
  font-weight: 600;
`;

const TooltipIcon = styled.span`
  color: ${themeGet("colors.greyLight")};
  cursor: help;
  font-size: 12px;

  &:hover {
    color: ${themeGet("colors.grey")};
  }
`;

const Description = styled.div`
  font-size: 11px;
  color: ${themeGet("colors.grey")};
  margin-top: 2px;
`;

const DefaultValue = styled.span`
  font-size: 10px;
  color: ${themeGet("colors.greyLight")};
  font-style: italic;
`;

const ArrayInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ArrayItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${themeGet("colors.grey")};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    color: ${themeGet("colors.danger")};
    background: ${themeGet("colors.dangerLightest")};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${themeGet("colors.greyLightest")};
  border: 1px dashed ${themeGet("colors.greyLight")};
  border-radius: 4px;
  color: ${themeGet("colors.grey")};
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: ${themeGet("colors.greyLighter")};
    border-color: ${themeGet("colors.grey")};
    color: ${themeGet("colors.greyDark")};
  }
`;

const EmptySchema = styled.div`
  text-align: center;
  padding: ${themeGet("space.r")};
  color: ${themeGet("colors.grey")};
  font-size: 13px;
`;

const ObjectArrayContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ObjectArrayItem = styled.div`
  border: 1px solid ${themeGet("colors.greyLighter")};
  border-radius: 6px;
  padding: 12px;
  background: ${themeGet("colors.greyLightest")};
  position: relative;
`;

const ObjectArrayItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
`;

const ObjectArrayItemTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${themeGet("colors.greyDark")};
`;

const FieldMappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const ArrowIcon = styled.span`
  color: ${themeGet("colors.grey")};
  font-size: 14px;
`;

const InlineFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InlineLabel = styled.label`
  font-size: 11px;
  color: ${themeGet("colors.grey")};
`;

const OptionalFieldsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: none;
  border: none;
  color: ${themeGet("colors.grey")};
  font-size: 11px;
  cursor: pointer;

  &:hover {
    color: ${themeGet("colors.greyDark")};
  }
`;

const OptionalFieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed ${themeGet("colors.greyLighter")};
`;

const MiniFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 120px;
  flex: 1;
  max-width: 200px;
`;

/**
 * Render a string input field
 */
function StringField({ name, schema, value, onChange, isRequired }) {
  const handleChange = useCallback(
    (e) => {
      onChange(name, e.target.value || undefined);
    },
    [name, onChange]
  );

  // Check if this is an enum (select)
  if (schema.enum) {
    const options = schema.enum.map((val) => ({
      value: val,
      label: val,
    }));

    return (
      <Select
        inputId={`config-${name}`}
        options={options}
        value={options.find((o) => o.value === value) || null}
        onChange={(option) => onChange(name, option?.value)}
        placeholder={
          schema.default ? `Default: ${schema.default}` : "Select..."
        }
        isClearable={!isRequired}
      />
    );
  }

  return (
    <TextInput
      id={`config-${name}`}
      value={value || ""}
      onChange={handleChange}
      placeholder={schema.default ? `Default: ${schema.default}` : ""}
    />
  );
}

StringField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
};

/**
 * Render a boolean toggle field
 */
function BooleanField({ name, schema, value, onChange }) {
  const handleChange = useCallback(() => {
    const newValue = value === undefined ? !schema.default : !value;
    onChange(name, newValue);
  }, [name, value, schema.default, onChange]);

  const isChecked = value !== undefined ? value : schema.default || false;

  return (
    <Toggle
      id={`config-${name}`}
      checked={isChecked}
      onChange={handleChange}
      label={isChecked ? "Yes" : "No"}
    />
  );
}

BooleanField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

/**
 * Render an array input field (for string arrays)
 */
function ArrayField({ name, value, onChange }) {
  const items = useMemo(() => value || [], [value]);
  const keysRef = useRef([]);
  if (keysRef.current.length > items.length) {
    keysRef.current = keysRef.current.slice(0, items.length);
  }
  while (keysRef.current.length < items.length) {
    keysRef.current.push(generateId());
  }

  const handleAddItem = useCallback(() => {
    onChange(name, [...items, ""]);
  }, [name, items, onChange]);

  const handleRemoveItem = useCallback(
    (index) => {
      const newItems = items.filter((_, i) => i !== index);
      onChange(name, newItems.length > 0 ? newItems : undefined);
    },
    [name, items, onChange]
  );

  const handleItemChange = useCallback(
    (index, newValue) => {
      const newItems = [...items];
      newItems[index] = newValue;
      onChange(name, newItems);
    },
    [name, items, onChange]
  );

  return (
    <ArrayInput>
      {items.map((item, index) => (
        <ArrayItemRow key={keysRef.current[index]}>
          <Box flex="1">
            <TextInput
              id={`config-${name}-${index}`}
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              placeholder={`Item ${index + 1}`}
            />
          </Box>
          <RemoveButton
            type="button"
            onClick={() => handleRemoveItem(index)}
            title="Remove item"
          >
            <FontAwesomeIcon icon={["fas", "times"]} />
          </RemoveButton>
        </ArrayItemRow>
      ))}
      <AddButton type="button" onClick={handleAddItem}>
        <FontAwesomeIcon icon={["fas", "plus"]} />
        Add Item
      </AddButton>
    </ArrayInput>
  );
}

ArrayField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

/**
 * Check if schema represents a field mapping pattern (has from/to properties)
 */
function isFieldMappingSchema(itemsSchema) {
  if (!itemsSchema?.properties) {
    return false;
  }
  const props = Object.keys(itemsSchema.properties);
  return props.includes("from") && props.includes("to");
}

/**
 * Render a single field mapping item with source -> target layout
 */
function FieldMappingItem({
  index,
  item,
  itemsSchema,
  onChange,
  onRemove,
  availableSourceFields,
}) {
  const [showOptional, setShowOptional] = useState(false);

  const handleFieldChange = useCallback(
    (fieldName, fieldValue) => {
      onChange(index, { ...item, [fieldName]: fieldValue });
    },
    [index, item, onChange]
  );

  // Get optional fields (not from/to)
  const optionalFields = useMemo(() => {
    if (!itemsSchema?.properties) {
      return [];
    }
    return Object.entries(itemsSchema.properties).filter(
      ([key]) => key !== "from" && key !== "to"
    );
  }, [itemsSchema]);

  const hasOptionalValues = useMemo(() => {
    return optionalFields.some(([key]) => item?.[key] !== undefined);
  }, [optionalFields, item]);

  // Build source field options for Select
  const sourceOptions = useMemo(() => {
    if (!availableSourceFields?.length) {
      return null;
    }
    return availableSourceFields.map((field) => ({
      value: field,
      label: field,
    }));
  }, [availableSourceFields]);

  return (
    <ObjectArrayItem>
      <ObjectArrayItemHeader>
        <ObjectArrayItemTitle>Mapping {index + 1}</ObjectArrayItemTitle>
        <RemoveButton type="button" onClick={() => onRemove(index)}>
          <FontAwesomeIcon icon={["fas", "times"]} />
        </RemoveButton>
      </ObjectArrayItemHeader>

      <FieldMappingRow>
        <InlineFieldGroup>
          <InlineLabel>Source Field</InlineLabel>
          {sourceOptions ? (
            <Select
              inputId={`mapping-${index}-from`}
              options={sourceOptions}
              value={sourceOptions.find((o) => o.value === item?.from) || null}
              onChange={(option) => handleFieldChange("from", option?.value)}
              placeholder="Select source field..."
              isClearable
              isCreatable
              formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
            />
          ) : (
            <TextInput
              id={`mapping-${index}-from`}
              value={item?.from || ""}
              onChange={(e) => handleFieldChange("from", e.target.value)}
              placeholder="e.g., Person ID"
            />
          )}
        </InlineFieldGroup>

        <ArrowIcon>
          <FontAwesomeIcon icon={["fas", "arrow-right"]} />
        </ArrowIcon>

        <InlineFieldGroup>
          <InlineLabel>Target Field</InlineLabel>
          <TextInput
            id={`mapping-${index}-to`}
            value={item?.to || ""}
            onChange={(e) => handleFieldChange("to", e.target.value)}
            placeholder="e.g., externalId"
          />
        </InlineFieldGroup>
      </FieldMappingRow>

      {optionalFields.length > 0 && (
        <>
          <OptionalFieldsToggle
            type="button"
            onClick={() => setShowOptional(!showOptional)}
          >
            <FontAwesomeIcon
              icon={["fas", showOptional ? "chevron-up" : "chevron-down"]}
            />
            {showOptional ? "Hide" : "Show"} optional settings
            {hasOptionalValues && " (configured)"}
          </OptionalFieldsToggle>

          {showOptional && (
            <OptionalFieldsContainer>
              {optionalFields.map(([fieldKey, fieldSchema]) => (
                <MiniFieldGroup key={fieldKey}>
                  <InlineLabel>
                    {fieldSchema.title || fieldKey}
                    {fieldSchema.description && (
                      <TooltipIcon title={fieldSchema.description}>
                        {" "}
                        <FontAwesomeIcon icon={["fas", "info-circle"]} />
                      </TooltipIcon>
                    )}
                  </InlineLabel>
                  {fieldSchema.enum ? (
                    <Select
                      inputId={`mapping-${index}-${fieldKey}`}
                      options={fieldSchema.enum.map((v) => ({
                        value: v,
                        label: v,
                      }))}
                      value={
                        fieldSchema.enum
                          .map((v) => ({ value: v, label: v }))
                          .find((o) => o.value === item?.[fieldKey]) || null
                      }
                      onChange={(option) =>
                        handleFieldChange(fieldKey, option?.value)
                      }
                      placeholder="Select..."
                      isClearable
                      menuPlacement="auto"
                    />
                  ) : fieldSchema.type === "boolean" ? (
                    <Toggle
                      id={`mapping-${index}-${fieldKey}`}
                      checked={item?.[fieldKey] || false}
                      onChange={() =>
                        handleFieldChange(fieldKey, !item?.[fieldKey])
                      }
                      label={item?.[fieldKey] ? "Yes" : "No"}
                      small
                    />
                  ) : (
                    <TextInput
                      id={`mapping-${index}-${fieldKey}`}
                      value={item?.[fieldKey] || ""}
                      onChange={(e) =>
                        handleFieldChange(fieldKey, e.target.value || undefined)
                      }
                      placeholder={
                        fieldSchema.default
                          ? `Default: ${fieldSchema.default}`
                          : ""
                      }
                    />
                  )}
                </MiniFieldGroup>
              ))}
            </OptionalFieldsContainer>
          )}
        </>
      )}
    </ObjectArrayItem>
  );
}

FieldMappingItem.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.object,
  itemsSchema: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  availableSourceFields: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Render an array of field mappings with enhanced UI
 */
function FieldMappingArrayField({
  name,
  value,
  onChange,
  itemsSchema,
  availableSourceFields,
}) {
  const items = useMemo(() => value || [], [value]);
  const keysRef = useRef([]);
  if (keysRef.current.length > items.length) {
    keysRef.current = keysRef.current.slice(0, items.length);
  }
  while (keysRef.current.length < items.length) {
    keysRef.current.push(items[keysRef.current.length]?._key ?? generateId());
  }

  const handleAddItem = useCallback(() => {
    onChange(name, [...items, { from: "", to: "", _key: generateId() }]);
  }, [name, items, onChange]);

  const handleRemoveItem = useCallback(
    (index) => {
      const newItems = items.filter((_, i) => i !== index);
      onChange(name, newItems.length > 0 ? newItems : undefined);
    },
    [name, items, onChange]
  );

  const handleItemChange = useCallback(
    (index, newItem) => {
      const newItems = [...items];
      newItems[index] = newItem;
      onChange(name, newItems);
    },
    [name, items, onChange]
  );

  return (
    <ObjectArrayContainer>
      {items.map((item, index) => (
        <FieldMappingItem
          key={item._key ?? keysRef.current[index]}
          index={index}
          item={item}
          itemsSchema={itemsSchema}
          onChange={handleItemChange}
          onRemove={handleRemoveItem}
          availableSourceFields={availableSourceFields}
        />
      ))}
      <AddButton type="button" onClick={handleAddItem}>
        <FontAwesomeIcon icon={["fas", "plus"]} />
        Add Mapping
      </AddButton>
    </ObjectArrayContainer>
  );
}

FieldMappingArrayField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  itemsSchema: PropTypes.object,
  availableSourceFields: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Render a generic array of objects field
 */
function ObjectArrayField({ name, value, onChange, itemsSchema }) {
  const items = useMemo(() => value || [], [value]);
  const properties = itemsSchema?.properties || {};
  const propEntries = Object.entries(properties);
  const keysRef = useRef([]);
  if (keysRef.current.length > items.length) {
    keysRef.current = keysRef.current.slice(0, items.length);
  }
  while (keysRef.current.length < items.length) {
    keysRef.current.push(items[keysRef.current.length]?._key ?? generateId());
  }

  const handleAddItem = useCallback(() => {
    const newItem = { _key: generateId() };
    // Initialize with empty values for required fields
    if (itemsSchema?.required) {
      itemsSchema.required.forEach((key) => {
        newItem[key] = "";
      });
    }
    onChange(name, [...items, newItem]);
  }, [name, items, onChange, itemsSchema]);

  const handleRemoveItem = useCallback(
    (index) => {
      const newItems = items.filter((_, i) => i !== index);
      onChange(name, newItems.length > 0 ? newItems : undefined);
    },
    [name, items, onChange]
  );

  const handleItemFieldChange = useCallback(
    (index, fieldName, fieldValue) => {
      const newItems = [...items];
      newItems[index] = { ...newItems[index], [fieldName]: fieldValue };
      // Clean up undefined values
      if (fieldValue === undefined || fieldValue === "") {
        delete newItems[index][fieldName];
      }
      onChange(name, newItems);
    },
    [name, items, onChange]
  );

  return (
    <ObjectArrayContainer>
      {items.map((item, index) => (
        <ObjectArrayItem key={item._key ?? keysRef.current[index]}>
          <ObjectArrayItemHeader>
            <ObjectArrayItemTitle>Item {index + 1}</ObjectArrayItemTitle>
            <RemoveButton type="button" onClick={() => handleRemoveItem(index)}>
              <FontAwesomeIcon icon={["fas", "times"]} />
            </RemoveButton>
          </ObjectArrayItemHeader>
          <Flex flexWrap="wrap" style={{ gap: "12px" }}>
            {propEntries.map(([propKey, propSchema]) => (
              <MiniFieldGroup
                key={propKey}
                style={{ minWidth: "140px", flex: "1 1 140px" }}
              >
                <InlineLabel>
                  {propSchema.title || propKey}
                  {itemsSchema?.required?.includes(propKey) && (
                    <RequiredBadge> *</RequiredBadge>
                  )}
                </InlineLabel>
                {propSchema.enum ? (
                  <Select
                    inputId={`${name}-${index}-${propKey}`}
                    options={propSchema.enum.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                    value={
                      propSchema.enum
                        .map((v) => ({ value: v, label: v }))
                        .find((o) => o.value === item?.[propKey]) || null
                    }
                    onChange={(option) =>
                      handleItemFieldChange(index, propKey, option?.value)
                    }
                    placeholder="Select..."
                    isClearable
                    menuPlacement="auto"
                  />
                ) : propSchema.type === "boolean" ? (
                  <Toggle
                    id={`${name}-${index}-${propKey}`}
                    checked={item?.[propKey] || false}
                    onChange={() =>
                      handleItemFieldChange(index, propKey, !item?.[propKey])
                    }
                    label={item?.[propKey] ? "Yes" : "No"}
                    small
                  />
                ) : (
                  <TextInput
                    id={`${name}-${index}-${propKey}`}
                    value={item?.[propKey] || ""}
                    onChange={(e) =>
                      handleItemFieldChange(
                        index,
                        propKey,
                        e.target.value || undefined
                      )
                    }
                    placeholder={propSchema.description || ""}
                  />
                )}
              </MiniFieldGroup>
            ))}
          </Flex>
        </ObjectArrayItem>
      ))}
      <AddButton type="button" onClick={handleAddItem}>
        <FontAwesomeIcon icon={["fas", "plus"]} />
        Add Item
      </AddButton>
    </ObjectArrayContainer>
  );
}

ObjectArrayField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  itemsSchema: PropTypes.object,
};

/**
 * Render an object field (show JSON for complex objects)
 */
function ObjectField({ name, value, onChange }) {
  const jsonValue = value ? JSON.stringify(value, null, 2) : "";

  const handleChange = useCallback(
    (e) => {
      try {
        const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
        onChange(name, parsed);
      } catch {
        // Invalid JSON, don't update
      }
    },
    [name, onChange]
  );

  return (
    <TextInput
      id={`config-${name}`}
      as="textarea"
      rows={4}
      value={jsonValue}
      onChange={handleChange}
      placeholder='{"key": "value"}'
      style={{ fontFamily: "monospace", fontSize: 12 }}
    />
  );
}

ObjectField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

/**
 * Render a number input field
 */
function NumberField({ name, schema, value, onChange }) {
  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      if (val === "") {
        onChange(name, undefined);
      } else {
        const num = parseFloat(val);
        if (!isNaN(num)) {
          onChange(name, num);
        }
      }
    },
    [name, onChange]
  );

  return (
    <TextInput
      id={`config-${name}`}
      type="number"
      value={value ?? ""}
      onChange={handleChange}
      placeholder={
        schema.default !== undefined ? `Default: ${schema.default}` : ""
      }
    />
  );
}

NumberField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

/**
 * Render a single config field based on its schema
 */
function ConfigField({
  name,
  schema,
  value,
  onChange,
  isRequired,
  availableSourceFields,
}) {
  const renderField = () => {
    switch (schema.type) {
      case "string":
        return (
          <StringField
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
            isRequired={isRequired}
          />
        );
      case "boolean":
        return (
          <BooleanField
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
          />
        );
      case "array":
        // Check if this is an array of objects with schema
        if (schema.items?.type === "object" && schema.items?.properties) {
          // Check if it's a field mapping pattern
          if (isFieldMappingSchema(schema.items)) {
            return (
              <FieldMappingArrayField
                name={name}
                value={value}
                onChange={onChange}
                itemsSchema={schema.items}
                availableSourceFields={availableSourceFields}
              />
            );
          }
          // Generic object array
          return (
            <ObjectArrayField
              name={name}
              value={value}
              onChange={onChange}
              itemsSchema={schema.items}
            />
          );
        }
        // Simple array of strings
        return <ArrayField name={name} value={value} onChange={onChange} />;
      case "object":
        return <ObjectField name={name} value={value} onChange={onChange} />;
      case "number":
      case "integer":
        return (
          <NumberField
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
          />
        );
      default:
        // Fallback to string input
        return (
          <StringField
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
            isRequired={isRequired}
          />
        );
    }
  };

  return (
    <FieldGroup>
      <LabelRow>
        <Label htmlFor={`config-${name}`}>
          {schema.title || name.replace(/([A-Z])/g, " $1").trim()}
        </Label>
        {isRequired && <RequiredBadge>Required</RequiredBadge>}
        {schema.description && (
          <TooltipIcon title={schema.description}>
            <FontAwesomeIcon icon={["fas", "info-circle"]} />
          </TooltipIcon>
        )}
      </LabelRow>
      {renderField()}
      {schema.description && <Description>{schema.description}</Description>}
      {schema.default !== undefined && (
        <DefaultValue>Default: {JSON.stringify(schema.default)}</DefaultValue>
      )}
    </FieldGroup>
  );
}

ConfigField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
  availableSourceFields: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Main ConfigForm component that renders a form based on JSON schema
 * @param {Object} props
 * @param {Object} props.schema - JSON schema for the form
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Callback when config changes
 * @param {string[]} [props.availableSourceFields] - Available fields from upstream steps for field mapping
 */
function ConfigForm({ schema, config, onChange, availableSourceFields }) {
  const handleFieldChange = useCallback(
    (fieldName, fieldValue) => {
      const newConfig = { ...config };
      if (fieldValue === undefined) {
        delete newConfig[fieldName];
      } else {
        newConfig[fieldName] = fieldValue;
      }
      onChange(newConfig);
    },
    [config, onChange]
  );

  // Sort properties: required first, then alphabetically
  const sortedProperties = useMemo(() => {
    if (!schema?.properties) {
      return [];
    }

    const required = new Set(schema.required || []);
    return Object.entries(schema.properties).sort(([aKey], [bKey]) => {
      const aRequired = required.has(aKey);
      const bRequired = required.has(bKey);
      if (aRequired && !bRequired) {
        return -1;
      }
      if (!aRequired && bRequired) {
        return 1;
      }
      return aKey.localeCompare(bKey);
    });
  }, [schema]);

  if (!schema?.properties || Object.keys(schema.properties).length === 0) {
    return (
      <EmptySchema>
        <FontAwesomeIcon
          icon={["fas", "cog"]}
          style={{ marginBottom: 8, opacity: 0.5 }}
        />
        <div>This component has no configuration options</div>
      </EmptySchema>
    );
  }

  const requiredFields = new Set(schema.required || []);

  return (
    <FormContainer>
      {sortedProperties.map(([name, propSchema]) => (
        <ConfigField
          key={name}
          name={name}
          schema={propSchema}
          value={config?.[name]}
          onChange={handleFieldChange}
          isRequired={requiredFields.has(name)}
          availableSourceFields={availableSourceFields}
        />
      ))}
    </FormContainer>
  );
}

ConfigForm.propTypes = {
  schema: PropTypes.shape({
    type: PropTypes.string,
    required: PropTypes.arrayOf(PropTypes.string),
    properties: PropTypes.object,
  }),
  config: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  availableSourceFields: PropTypes.arrayOf(PropTypes.string),
};

export default memo(ConfigForm);
